import React, { useRef } from 'react'
import useSize from './useSize'
import { scaleLinear, scaleBand } from 'd3-scale'
import { config } from './config'
import './styles.scss'

const f = new Intl.NumberFormat('de-CH')
const nf = new Intl.NumberFormat('de-CH')

interface Props extends answerTheQuestionProps {
  range: [number, number]
  step: number
  unit?: string
  formatValue?: (v: number) => string | number
}
export const ColumnChart = ({
  allAnswers,
  correctAnswer = 0,
  userAnswer,
  range,
  step,
  unit = '',
  formatValue
}: Props) => {
  let possibleValues = new Array((range[1] - range[0]) / step + 1)
    .fill(range[0])
    .map((m, i) => m + i * step)

  let max = Math.max.apply(Math, Object.values(allAnswers).map(Number))

  const valueFormat =
    formatValue ||
    ((val: number) => {
      return val > 9999 ? nf.format(val) : val
    })

  const [container, size] = useSize()
  const svg = useRef<SVGSVGElement>(null)
  //
  const width = size.width
  const height = 300
  const gutterTop = 50
  const gutterRight = 20
  const gutterLeft = 20
  const gutterBottom = 140
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleBand<number>()
        .domain(possibleValues)
        .range([plotArea.left, plotArea.right]),
    [size, userAnswer]
  )
  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain([0, max])
        .range([plotArea.top, plotArea.bottom]),
    [size, userAnswer]
  )

  let xTicks = [range[0], range[1]]
  userAnswer = userAnswer.replace('_', '.')
  let correct = +correctAnswer || 0
  let correctX = xAxisScale(correct) || 0
  let userX = xAxisScale(+userAnswer) || 0

  // if (
  //   Math.abs(userX - correctX) < config.iconWidth * 1.25 &&
  //   userAnswer?.toString() !== correctAnswer?.toString()
  // ) {
  //   if (correctX >= userX) {
  //     correctX = userX + config.iconWidth * 1.25
  //   } else if (correctX < userX) {
  //     userX = correctX + config.iconWidth * 1.25
  //   }
  // }
  let w = Math.round((plotArea.right - plotArea.left) / possibleValues.length)
  return (
    <div className='ColumnChart'>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        <svg
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
        >
          <text x={gutterLeft} y={20} className='yLabel'>
            Anzahl der Antworten
          </text>
          {xTicks.map((m, i) => {
            return (
              <text
                key={i}
                x={xAxisScale(m)}
                y={yAxisScale.range()[1] + 18 + plotArea.top}
                className='tick'
                textAnchor={
                  i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'
                }
              >
                {unit} {valueFormat(m)}
              </text>
            )
          })}
          {possibleValues.map((m, i) => {
            let h =
              yAxisScale(allAnswers[m.toString().replace('.', '_')] || 0) -
              plotArea.top

            return (
              <React.Fragment key={i}>
                <rect
                  x={xAxisScale(m)}
                  y={yAxisScale.range()[1] - h + plotArea.top}
                  width={w}
                  height={h}
                  className={
                    'column' +
                    (correct === m
                      ? ' corr'
                      : +userAnswer.replace('_', '.') === m
                      ? ' user'
                      : '')
                  }
                />
              </React.Fragment>
            )
          })}

          <line
            x1={xAxisScale.range()[0]}
            x2={xAxisScale.range()[1]}
            y1={yAxisScale.range()[1] + plotArea.top}
            y2={yAxisScale.range()[1] + plotArea.top}
            className='axis'
          />
          {userAnswer?.toString() !== correctAnswer?.toString() && (
            <g transform={'translate(' + userX + ',0)'}>
              <image
                href={require('../../_assets/icons_user.png')}
                x={-config.iconWidth / 2 + w / 2}
                y={yAxisScale.range()[1] + 30 + plotArea.top}
                height='30'
                width={config.iconWidth}
              />
              <text
                x={
                  w / 2 +
                  (userX < 50
                    ? -(config.iconWidth / 2)
                    : userX > xAxisScale.range()[1] - 50
                    ? config.iconWidth / 2
                    : 0)
                }
                y={yAxisScale.range()[1] + 78 + plotArea.top}
                className='value user'
                textAnchor={
                  userX < 50
                    ? 'left'
                    : userX > xAxisScale.range()[1] - 50
                    ? 'end'
                    : 'middle'
                }
              >
                {unit} {valueFormat(userAnswer)}
              </text>
            </g>
          )}
          <g transform={'translate(' + correctX + ',0)'}>
            <image
              href={require('../../_assets/icons_correct.png')}
              x={-config.iconWidth / 2 + w / 2}
              y={yAxisScale.range()[0] - 20}
              height='30'
              width={config.iconWidth}
            />
            <line
              x1={w / 2}
              x2={w / 2}
              y1={yAxisScale.range()[0] + 40}
              y2={yAxisScale.range()[1] + plotArea.top}
              className='correct'
            />
            <text
              x={
                w / 2 +
                (correctX < 50
                  ? -(config.iconWidth / 2)
                  : correctX > xAxisScale.range()[1] - 50
                  ? config.iconWidth / 2
                  : 0)
              }
              y={yAxisScale.range()[0] + 30}
              className='value corr'
              textAnchor={
                correctX < 50
                  ? 'left'
                  : correctX > xAxisScale.range()[1] - 50
                  ? 'end'
                  : 'middle'
              }
            >
              {unit} {valueFormat(correct)}
            </text>
          </g>
        </svg>
      </div>
    </div>
  )
}
